import { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Camera, Upload, Trash2, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

export default function ProfilePhotoUpload({ childId, currentPhotoUrl, onPhotoUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Maximum size is 5MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/children/${childId}/profile-photo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Profile photo updated!');
      if (onPhotoUpdate) {
        onPhotoUpdate(response.data.photo_url);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
      setShowMenu(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove profile photo?')) return;

    setDeleting(true);
    try {
      await axios.delete(`${API}/children/${childId}/profile-photo`);
      toast.success('Profile photo removed');
      if (onPhotoUpdate) {
        onPhotoUpdate(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove photo');
    } finally {
      setDeleting(false);
      setShowMenu(false);
    }
  };

  const photoUrl = currentPhotoUrl ? `${API}${currentPhotoUrl}` : null;

  return (
    <div className="relative" data-testid="profile-photo-upload">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Photo Display */}
      <div 
        className="relative group cursor-pointer"
        onClick={() => setShowMenu(!showMenu)}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt="Profile"
            className="w-20 h-20 rounded-full object-cover border-4 border-zinc-800 group-hover:border-[#02b5ff]/50 transition-all"
            data-testid="profile-photo"
          />
        ) : (
          <div className="w-20 h-20 bg-gradient-to-br from-[#02b5ff]/30 to-[#ff025f]/30 rounded-full flex items-center justify-center border-4 border-zinc-800 group-hover:border-[#02b5ff]/50 transition-all">
            <User className="h-10 w-10 text-[#02b5ff]" />
          </div>
        )}

        {/* Camera overlay on hover */}
        <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Camera className="h-6 w-6 text-white" />
        </div>

        {/* Loading spinner */}
        {(uploading || deleting) && (
          <div className="absolute inset-0 bg-black/70 rounded-full flex items-center justify-center">
            <Loader2 className="h-6 w-6 text-[#02b5ff] animate-spin" />
          </div>
        )}
      </div>

      {/* Action Menu */}
      {showMenu && !uploading && !deleting && (
        <div className="absolute top-full left-0 mt-2 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-10 overflow-hidden min-w-[160px]">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-zinc-800 flex items-center gap-2"
          >
            <Upload className="h-4 w-4 text-[#02b5ff]" />
            {currentPhotoUrl ? 'Change Photo' : 'Upload Photo'}
          </button>
          {currentPhotoUrl && (
            <button
              onClick={handleDelete}
              className="w-full px-4 py-2.5 text-left text-sm text-[#ff025f] hover:bg-[#ff025f]/10 flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Remove Photo
            </button>
          )}
        </div>
      )}

      {/* Click outside to close */}
      {showMenu && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowMenu(false)}
        />
      )}
    </div>
  );
}
